/**
 * Second factor input, shared by the sign-in challenge (/mfa) and step-up: authenticator code, security key / passkey
 * (WebAuthn) or a recovery code. The caller passes where to fetch WebAuthn options and what to do with the answer.
 */
import type { MfaMethod } from "@prop/contracts";
import { Button, Field, Input, Notice } from "@prop/ui";
import { startAuthentication } from "@simplewebauthn/browser";
import { useState } from "react";
import { useAuthT } from "../../i18n";
import { post } from "../../lib/api";
import { useAction } from "../../lib/useAction";

export type SecondFactorAnswer = { method: "totp"; code: string } | { method: "recovery"; code: string } | { method: "webauthn"; response: unknown };

export function SecondFactorForm({
  methods,
  optionsPath,
  submitLabel,
  onAnswer,
}: {
  methods: MfaMethod[];
  /** Where to get WebAuthn options (sign-in: /auth/mfa/webauthn/options, step-up: /auth/step-up/webauthn/options). */
  optionsPath: string;
  submitLabel: string;
  onAnswer: (a: SecondFactorAnswer) => Promise<unknown>;
}) {
  const t = useAuthT();
  const first: MfaMethod = methods.includes("webauthn") ? "webauthn" : methods.includes("totp") ? "totp" : "recovery";
  const [method, setMethod] = useState<MfaMethod>(first);
  const [code, setCode] = useState("");
  const action = useAction(async (answer: SecondFactorAnswer) => onAnswer(answer));
  const keyAction = useAction(async () => {
    const options = await post<Parameters<typeof startAuthentication>[0]["optionsJSON"]>(optionsPath);
    const response = await startAuthentication({ optionsJSON: options });
    await onAnswer({ method: "webauthn", response });
  });

  const others = methods.filter((m) => m !== method);
  const switchLabel = (m: MfaMethod) => (m === "webauthn" ? t("mfa.useKey") : m === "recovery" ? t("mfa.useRecovery") : t("mfa.useTotp"));
  const message = action.message ?? keyAction.message;

  return (
    <div className="flex flex-col gap-4">
      {method === "webauthn" ? (
        <Button type="button" size="lg" onClick={() => void keyAction.run()} disabled={keyAction.pending}>
          {keyAction.pending ? t("mfa.keyWaiting") : t("mfa.useKey")}
        </Button>
      ) : (
        <form
          className="flex flex-col gap-4"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            void action.run(method === "totp" ? { method: "totp", code: code.trim() } : { method: "recovery", code: code.trim() });
          }}
        >
          <Field label={method === "totp" ? t("mfa.totpLabel") : t("mfa.recoveryLabel")} htmlFor="mfa-code">
            <Input
              id="mfa-code"
              autoFocus
              autoComplete="one-time-code"
              inputMode={method === "totp" ? "numeric" : "text"}
              maxLength={method === "totp" ? 6 : 11}
              className="h-11 text-center text-lg tracking-[0.3em] tabular-nums md:text-lg"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </Field>
          <Button type="submit" size="lg" disabled={action.pending || (method === "totp" ? code.trim().length !== 6 : code.trim().length < 10)}>
            {submitLabel}
          </Button>
        </form>
      )}
      {message && <Notice tone="error">{message}</Notice>}
      {others.length > 0 && (
        <div className="flex flex-col items-center gap-2 border-t border-border pt-4">
          {others.map((m) => (
            <button
              key={m}
              type="button"
              className="min-h-11 text-sm text-muted-foreground underline-offset-4 hover:underline"
              onClick={() => {
                setMethod(m);
                setCode("");
                action.reset();
              }}
            >
              {switchLabel(m)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
